import { format } from 'date-fns';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FieldRow } from '@/modules/sentinel-workspace/field-row';
import { PayloadSection } from '@/modules/sentinel-workspace/tool-call-card';
import { ConversationTicketLink } from './conversation-ticket-link';
import type { ToolExecutionLog } from '@/services';

/** One recorded call, laid out like the Sentinel workspace details panel. */
export const ToolExecutionLogDetailsSidebar = ({ log, onClose }: { log: ToolExecutionLog; onClose: () => void }) => {
	return (
	<aside className='flex h-full w-[340px] shrink-0 flex-col border-l border-gray-500 bg-white'>
		<div className='flex h-[60px] shrink-0 items-center justify-between px-4 py-3'>
			<p className='text-[13px] font-medium leading-[18px] text-text-body'>Call details</p>
			<Button variant='ghost' size='icon-sm' onClick={onClose} className='text-gray-900' aria-label='Close'>
				<X className='size-[14px]' />
			</Button>
		</div>
		<div className='h-px w-full shrink-0 bg-gray-500' />
		<div className='relative flex-1 overflow-y-auto [scrollbar-gutter:stable]'>
			<div className='flex flex-col gap-3 p-4'>
				<div className='flex w-full items-center justify-between gap-5'>
					<p className='shrink-0 text-[12px] font-medium leading-[1.1] text-gray-700'>Status</p>
					<Badge variant={log.status === 'success' ? 'success' : 'destructive'}>
						{log.statusCode}
						{log.errorCode && ` · ${log.errorCode}`}
					</Badge>
				</div>
				<FieldRow label='Time' value={format(new Date(log.createdAt), 'd MMM yyyy, HH:mm:ss')} />
				<FieldRow label='Latency' value={`${log.latencyMs} ms`} />
				{log.conversationId != null && (
					<div className='flex w-full items-center justify-between gap-5'>
						<p className='shrink-0 text-[12px] font-medium leading-[1.1] text-gray-700'>Conversation</p>
						<span className='text-[12px] leading-[1.1] text-text-body'>
							<ConversationTicketLink log={log} />
						</span>
					</div>
				)}
				<FieldRow label='Request ID' value={log.requestId} />
				{log.errorMessage && <p className='text-[12px] leading-4 break-words text-red-500'>{log.errorMessage}</p>}
			</div>
			<div className='h-px w-full bg-gray-500' />
			<div className='flex flex-col gap-3 p-4'>
				<PayloadSection title='INPUT' payload={log.input} />
				<PayloadSection title='OUTPUT' payload={log.output} />
			</div>
		</div>
	</aside>
	);
};
